import type { Session as SupabaseSession, User as SupabaseUser } from '@supabase/supabase-js';

import { ENV } from '@/shared/config/env';
import { supabase } from '@/shared/lib/supabase';
import type {
  ErrorResponse,
  GetMeResponse,
  MagicLinkApiResponse,
  MagicLinkRequest,
  PendingStatusResponse,
  UpdateProfileResponse,
} from '@/shared/types/auth';

import { apiClient } from './client';

/**
 * Thrown by AuthService.sendEmailOTP — carries the HTTP status returned by
 * Supabase's auth API so the UI can show a tailored message for 429 rate
 * limits versus generic errors.
 */
export class OtpSendError extends Error {
  status?: number;
  /**
   * Supabase's stable error code (e.g. `over_email_send_rate_limit`). Forwarded
   * so the auth flow can classify a 429 — the per-email cooldown and the
   * project-wide hourly cap need very different waits. See `otp-cooldown.ts`.
   */
  code?: string;
  constructor(message: string, status?: number, code?: string) {
    super(message);
    this.name = 'OtpSendError';
    this.status = status;
    this.code = code;
  }
}

export class AuthService {
  constructor() {
    // baseURL is set in the ENV configuration
  }

  /**
   * Get current user information
   * GET /auth/me
   * @param skipInterceptor - Skip the auth interceptor refresh logic (used during auth initialization)
   * @param silent - Suppress the global "Network error" / response-error toast.
   *                 Callers (e.g. the /auth/callback page) own their own error
   *                 UI and don't want a misleading toast on transient failures.
   */
  async getMe(skipInterceptor: boolean = false, silent: boolean = false): Promise<GetMeResponse> {
    try {
      const config: Record<string, unknown> = {};
      if (skipInterceptor) {
        config.headers = {};
        config._skipInterceptor = true;
      }
      if (silent) {
        config._silent = true;
      }
      const response = await apiClient.get<GetMeResponse>('/auth/me', config);
      return response.data;
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'response' in error) {
        throw (error as { response: { data: ErrorResponse } }).response.data;
      }
      throw error;
    }
  }

  /**
   * Lightweight account-status rehydrate for the closed-beta holding page.
   * GET /auth/pending-status
   *
   * Pending users are blocked from /auth/me by ApprovedAccountGuard, so the
   * holding page polls this endpoint on window focus to detect approval and
   * transition out. Returns just `{ accountStatus, statusChangedAt }` — the
   * full user payload comes back through getMe() once approved.
   */
  async getPendingStatus(): Promise<PendingStatusResponse> {
    try {
      const response = await apiClient.get<PendingStatusResponse>('/auth/pending-status');
      return response.data;
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'response' in error) {
        throw (error as { response: { data: ErrorResponse } }).response.data;
      }
      throw error;
    }
  }

  /**
   * Get current user information with a specific token
   * GET /auth/me
   * @param token - The access token to use for authentication
   */
  async getMeWithToken(token: string): Promise<GetMeResponse> {
    try {
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        _skipInterceptor: true,
      };
      const response = await apiClient.get<GetMeResponse>('/auth/me', config);
      return response.data;
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'response' in error) {
        throw (error as { response: { data: ErrorResponse } }).response.data;
      }
      throw error;
    }
  }

  /**
   * Send magic link for authentication
   * POST /auth/magic-link
   */
  async sendMagicLink(data: MagicLinkRequest): Promise<MagicLinkApiResponse> {
    try {
      const response = await apiClient.post<MagicLinkApiResponse>('/auth/magic-link', data);
      return response.data;
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'response' in error) {
        throw (error as { response: { data: ErrorResponse } }).response.data;
      }
      throw error;
    }
  }

  /**
   * Send OTP code via email using Supabase.
   *
   * Throws a typed `OtpSendError` so callers can distinguish a rate-limit
   * (429) from a generic failure and surface user-actionable copy. Supabase's
   * @supabase/supabase-js v2 returns AuthError with `status` populated for
   * HTTP-mapped failures — we forward that status field.
   */
  async sendEmailOTP(email: string): Promise<{ success: boolean; message: string }> {
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          shouldCreateUser: true,
          // Explicitly request email change type to get a 6-digit code
          emailRedirectTo: undefined, // Ensure no redirect URL is set
        },
      });

      if (error) {
        // Preserve the HTTP status + Supabase error code so the caller can
        // distinguish the per-email cooldown from the hourly cap (both 429).
        throw new OtpSendError(
          error.message || 'Failed to send OTP',
          error.status,
          (error as { code?: string }).code,
        );
      }

      return {
        success: true,
        message: 'Verification code sent to your email!',
      };
    } catch (error) {
      if (error instanceof OtpSendError) throw error;
      throw new OtpSendError(
        error instanceof Error ? error.message : 'Failed to send OTP',
        undefined,
      );
    }
  }

  /**
   * Verify OTP code using Supabase
   */
  async verifyEmailOTP(
    email: string,
    token: string,
  ): Promise<{ user: SupabaseUser; session: SupabaseSession }> {
    try {
      const { data, error } = await supabase.auth.verifyOtp({
        email,
        token,
        type: 'email',
      });

      if (error) {
        throw new Error(error.message);
      }

      if (!data.user || !data.session) {
        throw new Error('Invalid verification code');
      }

      return {
        user: data.user,
        session: data.session,
      };
    } catch (error) {
      throw new Error(error instanceof Error ? error.message : 'Failed to verify OTP');
    }
  }

  /**
   * Sign in with Google OAuth using Supabase.
   *
   * The redirect URI is computed from `window.location.origin` so a
   * localhost engineer onboarding the new flow lands back on
   * http://localhost:5173/auth/callback rather than the baked-in
   * VITE_APP_URL (which always points at preprod / production). The
   * Funnel agent flagged the BLOCKED variant: Google OAuth from
   * localhost deposited the user on preprod, where the new code wasn't
   * deployed yet.
   */
  async signInWithGoogle(): Promise<{ success?: boolean; error?: string }> {
    try {
      const redirectUrl = typeof window !== 'undefined'
        ? window.location.origin
        : ENV.APP_URL;

      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${redirectUrl}/auth/callback`,
          queryParams: {
            prompt: 'select_account',
          },
        },
      });

      if (error) {
        throw new Error(error.message);
      }

      return { success: true };
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Failed to sign in with Google',
      };
    }
  }

  /**
   * Register/create user after OAuth login
   * POST /auth/register
   *
   * @param silent - Suppress the global "Network error" / response-error toast.
   *                 Callers (e.g. the /auth/callback page) own their own error
   *                 UI and don't want a misleading toast on transient failures.
   */
  async registerOAuthUser(silent: boolean = false): Promise<GetMeResponse> {
    try {
      // `_silent` is a custom flag consumed by the axios response interceptor —
      // it's not in AxiosRequestConfig, so widen the type the same way
      // getMe() does for `_skipInterceptor` / `_silent` further up the file.
      const config: Record<string, unknown> = {};
      if (silent) config._silent = true;
      const response = await apiClient.post<GetMeResponse>('/auth/register', {}, config);
      return response.data;
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'response' in error) {
        throw (error as { response: { data: ErrorResponse } }).response.data;
      }
      throw error;
    }
  }

  /**
   * Update user profile settings
   * PATCH /user/profile
   */
  async updateProfile(data: { autoRefillEnabled?: boolean }): Promise<UpdateProfileResponse> {
    try {
      const response = await apiClient.patch<UpdateProfileResponse>('/user/profile', data);
      return response.data;
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'response' in error) {
        throw (error as { response: { data: ErrorResponse } }).response.data;
      }
      throw error;
    }
  }

  /**
   * Persist onboarding wizard state on the user profile.
   * PATCH /user/profile
   *
   * Backend accepts `onboarding_intent` and `onboarded_at` fields per the
   * profile DTO updated alongside the onboarding-redesign migration. Both
   * are independently optional so we can write the intent on step 1 and
   * the completed timestamp on the wizard exit without re-sending state.
   */
  async updateOnboardingState(state: {
    onboarding_intent?: 'creator' | 'developer';
    onboarded_at?: string;
  }): Promise<void> {
    await apiClient.patch('/user/profile', state);
  }

  /**
   * Delete user account permanently
   * POST /user/unregister
   */
  async unregisterUser(): Promise<{ success: boolean; message: string }> {
    try {
      const response = await apiClient.post('/user/unregister', {});

      // Check if response is successful
      if (response.status >= 200 && response.status < 300) {
        return {
          success: true,
          message: 'Account deleted successfully',
        };
      } else {
        return {
          success: false,
          message: 'Failed to delete account',
        };
      }
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'response' in error) {
        const errorResponse = (error as { response: { data: ErrorResponse } }).response.data;
        throw new Error(errorResponse.message || 'Failed to delete account');
      }
      throw new Error('Failed to delete account');
    }
  }
}

// Export singleton instance
export const authService = new AuthService();
